<template>
  <div class="mainContainer">
    <div
      class="topSection"
      :class="{ 'topSection--branded': companyReady && showBrandedPanel }"
      :style="companyReady ? topSectionStyle : {}">
      <!--
        Unified composition used for both the default branded panel and the
        client custom-banner override. Client logo (if set) sits as an absolute
        overlay on the left; the Envizion brandmark + slogan is always centered.
        Background rendering differs by branch: the `topSection--branded` class
        applies the solid Castleton Green + radial gradient; otherwise the
        `topSectionStyle` inline style paints the client banner image.

        Both the class, the inline style, and the inner content are gated on
        `companyReady` (i.e. `company.id` present). That prevents a flash of
        the branded panel during the ~500ms window between component mount and
        the reloadCompanyInfo API response resolving — without a gate, the
        default empty-customUI state renders the green panel briefly before
        the real bannerUrl arrives and replaces it.
      -->
      <div v-if="companyReady" class="topSection__inner" style="height: 200px;">
        <div
          v-if="company.customUI && company.customUI.clientLogoUrl"
          class="topSection__client-overlay">
          <v-img contain :src="company.customUI.clientLogoUrl" max-width="140" max-height="100"></v-img>
        </div>
        <div class="topSection__branding">
          <v-img
            :src="brandmarkSrc"
            alt="Envizion"
            contain
            max-width="120"
            class="topSection__brandmark" />
          <div class="topSection__tagline">
            <span class="topSection__tagline-label">Asset Visibility</span>
            <span class="topSection__tagline-separator">&mdash;</span>
            <span class="topSection__tagline-label">Anywhere</span>
          </div>
        </div>
      </div>
    </div>

    <v-card
      flat
      class="pa-3 d-flex flex-column facilitiesMainContainer"
      :style="`top: 200px`">
      <v-card
        class="rounded ma-0 d-flex flex-column flex-grow-1 pa-5"
        :class="{
          [`elevation-2 ${$vuetify.theme.isDark ? 'bg2' : 'white'}`]: listView == 'list',
          'transparent elevation-0': listView == 'cards',
          'pa-2': $vuetify.breakpoint.sm || $vuetify.breakpoint.xs,
          'pa-5': !$vuetify.breakpoint.sm || !$vuetify.breakpoint.xs,
        }"
        max-height="-webkit-fill-available">
        <div class="d-flex justify-space-between align-center"
          :class="{ 'flex-column': $vuetify.breakpoint.xs || $vuetify.breakpoint.sm }">
          <div class="d-flex flex-row col">
            <h2 class="headLine">Manage {{ listView == 'client' ? 'Clients' : 'Facilities' }}
            </h2>
            <version></version>
          </div>
          <div class="d-flex flex-row align-center justify-center col">
            <v-text-field
              class="me-2"
              :class="$vuetify.breakpoint.sm || $vuetify.breakpoint.xs ? 'flex-grow-1' : ''"
              :style="`flex:${
                $vuetify.breakpoint.sm || $vuetify.breakpoint.xs ? '1 1 auto' : 'none'
              }`"
              outlined
              rounded
              dense
              solo
              hide-details
              v-model="options.search"
              placeholder="Search..."
              append-icon="mdi-magnify">
            </v-text-field>
          </div>
          <div
            class="viewArea d-flex align-center justify-end col"
            :class="{ 'align-self-end': $vuetify.breakpoint.xs || $vuetify.breakpoint.sm }">
            <div class="d-flex flex-row align-center justify-center mr-4">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    rounded
                    color="primary"
                    @click="update()"
                    :disabled="!canCreateFacility || isLimitReached"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>mdi-plus</v-icon>
                    {{ $vuetify.breakpoint.xs ? 'Add facility' : 'Add a new facility' }}
                  </v-btn>
                </template>
                <span v-if="!canCreateFacility">You do not have permission to access this feature</span>
                <span v-else-if="isLimitReached">You have reached the maximum limit of {{ company.facilitiesLimitCount }} facilities</span>
                <span v-else-if="company.facilitiesLimitCount === -1">Add a new facility (unlimited facilities allowed)</span>
                <span v-else>Add a new facility</span>
              </v-tooltip>
            </div>
            <label class="mx-2"> <strong>View As</strong></label>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-2 pa-0"
                  width="44"
                  height="44"
                  max-width="40"
                  x-small
                  @click="setListView('cards')"
                  :color="listView == 'cards' ? 'primary' : ''"
                  v-bind="attrs"
                  v-on="on">
                  <v-icon v-if="listView == 'cards'" class="px-2">mdi-view-grid-outline</v-icon>
                  <v-icon v-else class="px-2" color="primary">mdi-view-grid-outline</v-icon>
                </v-btn>
              </template>
              <span>Grid</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-2 pa-0"
                  width="44"
                  height="44"
                  max-width="40"
                  x-small
                  @click="setListView('list')"
                  :color="listView == 'list' ? 'primary' : ''"
                  v-bind="attrs"
                  v-on="on">
                  <v-icon v-if="listView == 'list'" class="px-2">mdi-view-list-outline</v-icon>
                  <v-icon v-else class="px-2" color="primary">mdi-view-list</v-icon>
                </v-btn>
              </template>
              <span>List</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-2 pa-0"
                  width="44"
                  height="44"
                  max-width="40"
                  x-small
                  :disabled="!$has(permissions.Clients.View)"
                  @click="setListView('client')"
                  :color="listView == 'client' ? 'primary' : ''"
                  v-bind="attrs"
                  v-on="on">
                  <v-icon v-if="listView == 'client'" class="px-2">mdi-domain</v-icon>
                  <v-icon v-else class="px-2" color="primary">mdi-domain</v-icon>
                </v-btn>
              </template>
              <span>Client View</span>
            </v-tooltip>
          </div>
        </div>
        <div
          class="d-flex"
          :class="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm ? 'flex-column' : ''">
          <v-tabs
            v-model="selectedTab"
            :class="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm ? 'auto my-2' : 'w-50'">
            <v-tab v-for="(type, k) in filterTypes" :key="`filterType-${k}`">
              <v-icon class="me-2" v-if="$vuetify.theme.isDark">{{ type.icon }}</v-icon>
              <v-icon class="me-2" v-else>{{ type.lightIcon }}</v-icon>
              {{ type.text }}
            </v-tab>
          </v-tabs>

          <div
            class="d-flex align-center flex-wrap"
            :class="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm ? 'my-2' : ''">
            <div class="d-flex align-center">
              <v-icon class="mx-2" color="lightBlue"> mdi-information-outline </v-icon>
              <strong style="color: var(--v-lightBlue-base)">
                Maximum limit: {{ company.facilitiesLimitCount === -1 ? 'Unlimited' : company.facilitiesLimitCount }} Facilities
              </strong>
            </div>
            <v-spacer></v-spacer>
            <div class="ml-4 mt-1" style="width: 250px">
              <CountProgress
                :count="company.facilitiesLimitUsed || 0"
                :limit="company.facilitiesLimitCount || 0"
                :unlimited="company.facilitiesLimitCount === -1"
                title="Facilities Usage"
                icon="mdi-office-building"
                size="small"
              />
            </div>
          </div>
        </div>
        <v-divider></v-divider>

        <!-- Client Grouped View -->
        <div v-if="listView === 'client'" v-has="permissions.Clients.View" class="pa-5 overflow-y-auto d-flex flex-column flex-grow-1">
          <ClientGroupedView
            :facilities="entries"
            :loading="loading"
            :list-view="listView === 'client' ? 'cards' : listView"
            @facility-selected="openFacility"
            @toggle-favorite="toggleFavorite"
            @update-facility="update"
            @delete-facility="del" />
        </div>

        <!-- Original List/Cards View -->
        <v-data-iterator
          v-else
          class="elevation-0 rounded-0 d-flex flex-column justify-space-between table-sticky custom-table flex-grow-1"
          :headers="facilityHeaders"
          :items="entries"
          :options.sync="options"
          hide-default-footer
          :loading="loading"
          :server-items-length="total">
          <template v-slot:default="{ items }">
            <div
              class="pa-5 overflow-y-auto d-flex flex-column flex-grow-1"
              style="gap: 1rem"
              v-if="listView == 'list'">
              <div v-for="facility in items" :key="'p' + facility.id" style="width: 100%">
                <v-card
                  class="custom-card px-5"
                  elevation="1"
                  @click="openFacility(facility.id)"
                  :class="{ 'py-5': $vuetify.breakpoint.xs || $vuetify.breakpoint.sm }">
                  <v-row
                    align="center"
                    no-gutters
                    :class="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm ? 'flex-column' : ''">
                    <v-img
                      :src="getImage(facility.imageUrl)"
                      :max-width="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm ? 200 : 100" />

                    <v-col :cols="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm ? '12' : '2'">
                      <v-card-title
                        class="card-title"
                        :class="{
                          'justify-center': $vuetify.breakpoint.xs || $vuetify.breakpoint.sm,
                        }"
                        >{{ facility.name }}</v-card-title
                      >
                      <v-card-subtitle class="card-subtitle d-flex pt-2">
                        <img
                          class="me-2"
                          src="@public/img/user.svg"
                          contain
                          width="15"
                          height="20" />
                        <span class="subHeadLine">
                          Created by:
                          <span class="ms-2" v-if="facility.createdBy"
                            >{{ facility.createdBy.firstName + ' ' + facility.createdBy.lastName }}
                          </span>
                        </span>
                      </v-card-subtitle>
                      <v-card-subtitle class="card-subtitle d-flex pt-0" v-if="facility.client">
                        <img
                          class="me-2"
                          src="@public/img/user.svg"
                          contain
                          width="15"
                          height="20" />
                        <span class="subHeadLine">
                          Client:
                          <span class="ms-2">{{ facility.client.name }}</span>
                        </span>
                      </v-card-subtitle>
                    </v-col>

                    <v-spacer></v-spacer>

                    <v-col cols="auto">
                      <div class="d-flex flex-column">
                        <div class="actionButtons d-flex justify-space-between px-2 py-3">
                          <v-icon color="green">mdi-pencil</v-icon>
                          <v-icon
                            @click.stop="toggleFavorite(facility)"
                            v-if="facility.isFavorite"
                            color="infoColor"
                            >mdi-star-circle</v-icon
                          >
                          <v-icon @click.stop="toggleFavorite(facility)" v-else color="infoColor"
                            >mdi-star-outline</v-icon
                          >
                          <v-icon color="red">mdi-delete</v-icon>
                        </div>
                        <label class="subHeadLine">
                          <v-icon class="subHeadLine">mdi-clock-outline</v-icon> Created on:
                          24/07/2024</label
                        >
                      </div>
                    </v-col>
                  </v-row>
                </v-card>
              </div>
            </div>
            <v-row class="overflow-y-auto" v-else>
              <v-col v-for="facility in items" :key="facility.id" cols="12" sm="6" md="4" lg="2">
                <v-hover v-slot="{ hover }">
                  <v-card
                    :class="{
                      bg2: !facility.imageUrl,
                    }"
                    :elevation="2"
                    @click="openFacility(facility.id)"
                    class="mx-auto my-8 rounded-lg gridCard">
                    <v-img
                      height="300"
                      :src="getImage(facility.imageUrl)"
                      class="white--text align-end"
                      gradient="to top, rgba(35, 35, 30, 1), rgba(0, 0, 0, 0)">
                      <div class="d-flex align-center flex-column card-img-footer">
                        <v-card-title
                          class="bg_force_white--text align-self-start"
                          :class="{ 'hovered-title': hover }"
                          >{{ facility.name }}</v-card-title
                        >

                        <v-card-subtitle class="bg_force_white--text align-self-start pt-0">
                          <v-chip small label color="primary" class="align-self-start pt-0 mr-4" v-if="facility.facilityGroup">
                            {{ facility.facilityGroup.name }}
                          </v-chip>
                          <v-chip small label color="warning" class="align-self-start pt-0" v-if="facility.client">
                            {{ facility.client.name }}
                          </v-chip>
                        </v-card-subtitle>
                        <v-expand-transition>
                          <div
                            class="d-inline-flex pr-4 align-self-end card-actions"
                            :class="{ 'card-actions-hovered': hover }"
                            v-if="hover"
                            style="gap: 8px; transition: 0.5s">
                            <v-btn
                              v-has="permissions.Facilities.Update"
                              @click.stop="update(facility.id)"
                              fab
                              small
                              class="mb-2"
                              :color="$vuetify.theme.isDark ? 'bg1' : 'white'">
                              <v-icon color="success" class="ma-0 pa-0"> mdi-pencil-outline</v-icon>
                            </v-btn>
                            <v-btn
                              :loading="favLoading == facility.id"
                              fab
                              small
                              class="mb-2"
                              :color="$vuetify.theme.isDark ? 'bg1' : 'white'"
                              @click.stop="toggleFavorite(facility)">
                              <v-icon v-if="facility.isFavorite" color="infoColor"
                                >mdi-star-circle</v-icon
                              >
                              <v-icon v-else color="infoColor">mdi-star-outline</v-icon>
                              <!-- <v-icon color="yellow darken-3" class="ma-0 pa-0"
                                >{{ facility.isFavorite ? 'fas' : 'far' }} fa-star</v-icon
                              > -->
                            </v-btn>
                            <v-btn
                              v-has="permissions.Facilities.Delete"
                              fab
                              small
                              class="mb-2"
                              @click.stop="del(facility.id)"
                              :color="$vuetify.theme.isDark ? 'bg1' : 'white'">
                              <v-icon color="error" class="ma-0 pa-0"> mdi-delete-outline</v-icon>
                            </v-btn>
                          </div>
                        </v-expand-transition>
                      </div>
                    </v-img>
                  </v-card>
                </v-hover>
              </v-col>
            </v-row>
          </template>
          <template v-slot:footer>
            <div
              class="d-flex align-center"
              :class="
                $vuetify.breakpoint.xs || $vuetify.breakpoint.sm
                  ? 'flex-column justify-center'
                  : 'justify-space-between'
              ">
              <div
                class="d-flex align-center py-2"
                :class="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm ? 'justify-center' : ''">
                <label>Show</label>
                <v-select
                  class="mx-2"
                  style="width: 80px"
                  :items="[10, 15, 30, 50, 100, 200, 300, 400, 500, -1]"
                  v-model="options.itemsPerPage"
                  value="10"
                  dense
                  hide-details
                  solo></v-select>
                <label>Per Page</label>
              </div>
              <v-pagination
                class="py-2"
                v-model="options.page"
                :length="paginationLength"
                :total-visible="7"
                circle></v-pagination>
            </div>
          </template>
          <template v-slot:no-data>
            <div class="text-center d-flex flex-column">
              <img width="300" src="/img/notFound.svg" class="py-5 my-8" style="opacity: 0.54" />
              <p class="font-weight-medium">No Facilities Available!</p>
            </div>
          </template>
          <template v-slot:loading>
            <div class="text-center d-flex flex-column">
              <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
              <p class="font-weight-medium">Loading Facilities...</p>
            </div>
          </template>
        </v-data-iterator>
        <EditFacility ref="editFacility" @facility-updated="reloadCompanyInfo"></EditFacility>
      </v-card>
    </v-card>
  </div>
</template>

<script>
  import facilityHeaders from '@Facility/config/facility.headers';
  import { mapGetters } from 'vuex';
  import EditFacility from '@Facility/components/EditFacility.vue';
  import ClientGroupedView from '@Facility/components/ClientGroupedView.vue';
  import perms from '@Share/config/permissions';
  import { $formatDate } from '@Utils';
  import version from '@Share/components/Version.vue';
  import permissions from '@Share/config/permissions';
  import { LandingPageView } from '@/scripts/js/forgeModels';
  import { $has } from '@/utils';
  import { ENVIZION_BRANDMARKS } from '@Share/config/logos';
  import { LogoVariant } from '@Share/config/enums';
  import CountProgress from '@/features/Company/components/CountProgress.vue';
  import companyService from '@/features/Company/service/company-profile.service.js';
  import { error } from '@/utils/debugLogger';

  export default {
    name: 'facility-list',
    data() {
      return {
        permissions,
        facilityHeaders,
        perms,
        timerId: null,
        company:{
          customUI:{},
          owner:{}
        },
        filterTypes: [
          {
            text: 'All',
            value: 1,
            icon: 'mdi-view-grid-outline',
            lightIcon: 'mdi-view-grid-outline',
          },
          {
            text: 'Favorite',
            value: 2,
            icon: 'mdi-star',
            lightIcon: 'mdi-star-outline',
          },
          {
            text: 'Recent',
            value: 3,
            icon: 'mdi-history',
            lightIcon: 'mdi-history',
          },
        ],
        listView: 'cards',
        selectedTab: 0,
      };
    },
    computed: {
      ...mapGetters('FACILITY', {
        loading: 'GET_LOADING',
        entries: 'GET_ENTRIES',
        total: 'GET_TOTAL',
        selectedFacility: 'GET_FACILITY',
        favLoading: 'GET_FAV_LOADING',
      }),
      ...mapGetters('ACCOUNT', {
        user: 'GET_USER',
      }),
      options: {
        get() {
          return this.$store.getters['FACILITY/GET_OPTIONS'];
        },
        set(val) {
          this.$store.commit('FACILITY/SET_OPTIONS', val);
        },
      },
      paginationLength() {
        let paginationLength;
        if (this.total < this.options.itemsPerPage) {
          paginationLength = 1;
        } else {
          paginationLength = Math.ceil(this.total / this.options.itemsPerPage);
        }
        return paginationLength;
      },
      bannerUrl() {
        return this.company.customUI?.bannerUrl ? this.company.customUI.bannerUrl : require('../../../images/Facilities-BG.png');
      },
      showBrandedPanel() {
        return !this.company?.customUI?.bannerUrl;
      },
      topSectionStyle() {
        if (this.showBrandedPanel) return {};
        // Quote the URL so CSS url() parsing survives filenames containing
        // parentheses, spaces, commas, or other normally-allowed characters
        // that would otherwise terminate the url() value prematurely.
        return { backgroundImage: `url("${this.bannerUrl}")` };
      },
      /**
       * True once the company record has been hydrated from the API.
       * Used to gate top-section rendering so the default empty-customUI
       * state (which evaluates as "branded panel") doesn't flash before
       * the real bannerUrl / clientLogoUrl arrive.
       */
      companyReady() {
        return !!(this.company && this.company.id);
      },
      /**
       * Picks the Envizion brandmark ("E" symbol) variant for the top section.
       *
       * The COLORED→variant mapping differs by branch because the background
       * contexts differ:
       *   - Branded panel (fixed Castleton Green):
       *       COLORED  → Emerald        (bright green accent for contrast)
       *       else     → White          (high contrast for dark bg)
       *   - Custom banner (variable client background):
       *       COLORED  → CastletonGreen (dark brand green for light banners)
       *       else     → White          (for dark banners)
       *
       * The switching *logic* (reading customUI.avaLogoColor) is the same in
       * both branches — only the color resolution differs.
       */
      brandmarkSrc() {
        const preference = this.company && this.company.customUI
          ? this.company.customUI.avaLogoColor
          : null;
        if (this.showBrandedPanel) {
          return preference === LogoVariant.COLORED
            ? ENVIZION_BRANDMARKS.emerald
            : ENVIZION_BRANDMARKS.white;
        }
        return preference === LogoVariant.COLORED
          ? ENVIZION_BRANDMARKS.castletonGreen
          : ENVIZION_BRANDMARKS.white;
      },
      canCreateFacility() {
        return this.$has(this.permissions.Facilities.Create);
      },
      isLimitReached() {
        // If facilitiesLimitCount is -1, it means unlimited facilities
        return this.company.facilitiesLimitCount !== -1 &&
               this.company.facilitiesLimitCount > 0 &&
               this.company.facilitiesLimitUsed >= this.company.facilitiesLimitCount;
      }
    },
    methods: {
      $has,
      $formatDate,
      reloadCompanyInfo() {
        var companyId=this.user.companyId;
        companyService.getById(companyId)
          .then(({ data }) => {
           this.company=data;
          })
          .catch((err) => {
            error('Facilities', 'Failed to reload company info', err);
          });
      },
      update(facilityId = null) {
        this.$refs.editFacility.open(facilityId, true);
      },
      del(id) {
        this.$dialog.error({
          text: 'Are You Sure You Want to Delete This Facility?',
          title: 'Delete Facility',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                return this.$store.dispatch('FACILITY/DELETE', id)
                  .then(() => {
                    // Reload company info after successful deletion
                    this.reloadCompanyInfo();
                  });
              },
            },
          },
        });
      },
      openFacility(id) {
        this.$router.push({
          name: 'facility',
          params: { id },
        });
      },
      getDataDebounced() {
  if (this.timerId) {
    clearTimeout(this.timerId);
  }

  this.timerId = setTimeout(() => {
    const user = JSON.parse(localStorage.getItem('R-user'));
    if (user && user.id) {
      this.$store.dispatch('FACILITY/GET');
    }
    this.timerId = null;
  }, 400);
},
      toggleFavorite(facility) {
        this.$store.dispatch('FACILITY/TOGGLE_FAV', {
          id: facility.id,
          status: !facility.isFavorite,
        });
      },
            getImage(img) {
        let image = img ? img : require('../../../images/no-image-available.jpg');
        return image;
      },
      initializeViewFromPreferences() {
        return new Promise((resolve) => {

    const user = this.$store.getters['ACCOUNT/GET_USER'];

        if (user && user.userPreferences) {
          // If no current view, use default landing view preference
          if (user.userPreferences.defaultFacilitiesView === LandingPageView.Client && this.$has(this.permissions.Clients.View)) {
            this.listView = 'client';
          }
          else {
            this.listView = 'cards';
          }

        }

        resolve(this.listView);

        });

      },
      fetchDataBasedOnView() {
        if (this.listView === 'client') {
          this.getDataForClientView();
        } else {
          this.getDataDebounced();
        }
      },
      setListView(view) {
        // Check permission before switching to client view
        if (view === 'client' && !this.$has(this.permissions.Clients.View)) {
          return;
        }

        this.listView = view;
        // Trigger data refresh if switching to client view for optimized fetching
        if (view === 'client') {
          this.getDataForClientView();
        } else {
          this.getDataDebounced();
        }
      },
      getDataForClientView() {
        // Optimized data fetching for client view
        // Include client data in the request options
        let user = JSON.parse(localStorage.getItem('R-user'));
        const options = {
          ...this.options,
          includeClientData: true,
          groupByClient: true
        };
        this.$store.dispatch('FACILITY/GET_BY_CLIENT', { userId: user.id, options });
      },
    },
    created() {
      // Set initial view based on user preferences
      this.initializeViewFromPreferences().then(()=>{
        this.fetchDataBasedOnView();
      });
      this.reloadCompanyInfo();
    },

    watch: {
      options: {
        deep: true,
        handler() {
          this.fetchDataBasedOnView();
        },
      },
      selectedTab: {
        deep: true,
        handler(val, oldval) {
          if (val !== oldval) {
            this.options.filterType = val + 1;
          }
        },
      },
      // Watch for permission changes and reset view if user loses Clients.View permission
      '$store.state.user.permissions': {
        handler() {
          if (this.listView === 'client' && !this.$has(this.permissions.Clients.View)) {
            this.listView = 'cards';
          }
        }
      }
    },
    components: {
      EditFacility,
      ClientGroupedView,
      version,
      CountProgress,
    },
  };
</script>

<style lang="scss">
  .mainContainer {
    position: relative;
    height: 100%;
    background: var(--v-bg1-darken1);
    .topSection {
      background-size: cover;
      background-repeat: no-repeat;
      height: 37%;
      position: relative;
      overflow: hidden;

      // Default state: solid Castleton Green branded panel (no client banner override)
      &--branded {
        background: #03624C;

        // Subtle radial gradients (port of login right panel ambience)
        &::before {
          content: '';
          position: absolute;
          inset: 0;
          background:
            radial-gradient(circle at 20% 30%, rgba(44, 194, 149, 0.22) 0%, transparent 40%),
            radial-gradient(circle at 85% 70%, rgba(3, 71, 56, 0.4) 0%, transparent 45%);
          pointer-events: none;
          z-index: 0;
        }
      }

      &__inner {
        position: relative;
        z-index: 2;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &__client-overlay {
        position: absolute;
        left: 32px;
        top: 50%;
        transform: translateY(-50%);
        z-index: 3;
      }

      &__branding {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 14px;
        z-index: 2;
      }

      &__brandmark {
        width: 100%;
        max-width: 120px;
        height: auto;
      }

      @media (max-width: 600px) {
        &__brandmark {
          max-width: 90px;
        }
      }

      &__tagline {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 11px;
        letter-spacing: 0.28em;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.75);
      }

      &__tagline-separator {
        opacity: 0.4;
      }
    }
    .facilitiesMainContainer {
      position: absolute;
      height: calc(100% - 216px);
      top: 200px;
      left: 1%;
      width: 98%;
      .headLine {
        font-size: 24px;
        font-weight: 500;
        line-height: 36px;
        letter-spacing: -0.01em;
        color: var(--v-bg6-base);
      }
      .subHeadLine {
        font-size: 14px;
        font-weight: 400;
        line-height: 19.5px;
        letter-spacing: -0.01em;
        color: var(--v-bg6-lighten3);
      }
      .viewArea {
        label {
          color: var(--v-bg5-base);
        }
      }

      .gridCard {
        .card-img-footer {
          .v-card__title.hovered-title {
            font-size: 20px;
            transition: 0.5s;
          }
          .v-card__title {
            font-size: 24px;
            font-weight: 400;
            line-height: 33px;
            letter-spacing: -0.01em;
            transition: font-size 0.3s ease-in-out;
          }
        }
      }
      .v-item-group.v-slide-group.v-tabs-bar.primary--text {
        background-color: transparent;
      }
      .custom-card {
        .v-image {
          border-radius: 10px;
        }
      }
      .v-slide-group__prev.v-slide-group__prev--disabled {
        display: none !important;
      }
    }
  }
  .facilities-toolbar {
    width: 50%;
    flex: none;
  }

  .facility-card {
    // background: var(--v-bg3-base) !important;
    transition: all ease-out 0.25s;
    border: 1px solid #1e1e1e;

    .image-wrpr {
      width: 100px;
      height: 125px;
      transition: all ease-out 0.25s;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 0.5rem;
      overflow: hidden;
      &.full-width {
        width: 100%;
      }
      &.icon-wrpr {
        background: rgba(#fff, 0.08);
        color: rgba(var(--v-primary-base), 0.87);
        color: #fff;
      }

      &.image-wrpr-small {
        width: 54px;
        height: 54px;
        border-radius: 54px;
      }

      img {
        display: block;
        object-fit: cover;
        object-position: center;
        width: 100%;
        height: 100%;
        transform: scale(1);
        transition: all ease-out 0.25s;
      }

      i {
        font-size: 40px;
        transition: all ease-out 0.25s;
        transform: scale(1);
      }
    }

    .facility-actions {
      transition: all ease-out 0.25s;
    }

    &.v-card:hover {
      background: var(--v-bg1-base) !important;
      border-color: var(--v-primary-base) !important;

      .facility-actions {
        opacity: 1;
      }

      .image-wrpr {
        width: 100px;
        height: 125px;

        &.image-wrpr-small {
          width: 54px;
          height: 54px;
          border-radius: 54px;
        }
      }
    }

    &.opened {
      border-left: 3px solid rgba(var(--v-primary-base), 1) !important;
    }
  }
</style>
